import React, { useState, useEffect } from 'react';
import { Timeline, Typography, Row, Col, Spin, Tag, Empty, Card } from 'antd';
import { fetchData } from '../../service/dataServices';
import { getDateTimeDisplayText } from '../../../utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { getLocalizedName, getLocalizedTitle, localizeField } from '../../utils/localization';

const { Text } = Typography;

const getActionColor = (action) => {
    if (!action) return 'gray';
    switch (action.toUpperCase()) {
        case 'INSERT':
            return 'green';
        case 'UPDATE':
            return 'blue';
        case 'DELETE':
            return 'red';
        default:
            return 'gray';
    }
};

const getActionLabel = (action, t) => {
    if (!action) return t('changeHistory.modified');
    switch (action.toUpperCase()) {
        case 'INSERT':
            return t('changeHistory.created');
        case 'UPDATE':
            return t('changeHistory.updated');
        case 'DELETE':
            return t('changeHistory.deleted');
        default:
            return action.charAt(0).toUpperCase() + action.slice(1).toLowerCase();
    }
};

// Field-to-section mapping for grouping changes (translation keys)
const FIELD_SECTION_KEYS = {
    classification: 'objectSections.classification',
    category: 'objectSections.classification',
    inventoryNumber: 'objectSections.basicInformation',
    objectName: 'objectSections.basicInformation',
    objectTitle: 'objectSections.basicInformation',
    author: 'objectSections.basicInformation',
    quantity: 'objectSections.basicInformation',
    available: 'objectSections.basicInformation',
    availabilityPurpose: 'objectSections.basicInformation',
    mainImage: 'objectSections.media',
    imageList: 'objectSections.media',
    length: 'objectSections.dimensions',
    width: 'objectSections.dimensions',
    height: 'objectSections.dimensions',
    weight: 'objectSections.dimensions',
    otherDimensions: 'objectSections.dimensions',
    material: 'objectSections.materialsTechnique',
    technique: 'objectSections.materialsTechnique',
    region: 'objectSections.origin',
    country: 'objectSections.origin',
    city: 'objectSections.origin',
    century: 'objectSections.origin',
    date: 'objectSections.origin',
    description: 'objectSections.descriptionInscriptions',
    inscriptionType: 'objectSections.descriptionInscriptions',
    inscriptionPosition: 'objectSections.descriptionInscriptions',
    location: 'objectSections.location',
    locationArea: 'objectSections.location',
    displayArea: 'objectSections.location',
    showcaseNumber: 'objectSections.location',
    shelfNumber: 'objectSections.location',
    qrCodeRef: 'objectSections.location',
    hallId: 'objectSections.location',
    zoneId: 'objectSections.location',
    displayStationId: 'objectSections.location',
    registrationDate: 'objectSections.registration',
    recordedBy: 'objectSections.registration',
    userId: 'objectSections.registration',
};

// Fields that are internal/system-level
const SYSTEM_FIELDS = ['id', 'created_time', 'updated_time', 'created_at', 'updated_at', 'version'];

export default function ObjectChangeHistory({ entityId, refetchTrigger }) {
    const { t } = useTranslation();
    const [logs, setLogs] = useState([]);

    const { isLoading, refetch } = fetchData(
        `/api/audit/logs/entity`,
        (response) => {
            const logsData = response?.content || response || [];
            setLogs(logsData);
        },
        { tableName: 'object_overview', entityId },
        !!entityId
    );

    useEffect(() => {
        if (refetchTrigger) {
            refetch();
        }
    }, [refetchTrigger]);

    // Map a field key to its translated display name
    const getFieldDisplayName = (fieldKey) => {
        // Try to find a translation key under 'objects'
        const translationKey = `objects.${fieldKey}`;
        const translated = t(translationKey);
        // If translation exists (not the same as the key), use it
        if (translated !== translationKey) {
            return translated;
        }
        // Fallback: format the key nicely
        return fieldKey
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .replace(/^\w/, c => c.toUpperCase())
            .trim();
    };

    // Map a field key to its section name
    const getFieldSection = (fieldKey) => {
        // Check nested fields (e.g., "dropdowns.material")
        const baseField = fieldKey.split('.').pop();
        if (FIELD_SECTION_KEYS[baseField]) return t(FIELD_SECTION_KEYS[baseField]);
        if (FIELD_SECTION_KEYS[fieldKey]) return t(FIELD_SECTION_KEYS[fieldKey]);
        // Handle dropdowns.* pattern
        if (fieldKey.startsWith('dropdowns.')) return FIELD_SECTION_KEYS[baseField] ? t(FIELD_SECTION_KEYS[baseField]) : t('objectSections.dropdowns');
        return t('changeHistory.otherFields');
    };

    const isSystemField = (fieldKey) => {
        return SYSTEM_FIELDS.includes(fieldKey) || fieldKey === 'id';
    };

    // Group changes by section
    const groupChangesBySection = (changes) => {
        if (!changes || changes.length === 0) return {};

        const grouped = {};
        const systemChanges = [];
        const otherChanges = [];

        changes.forEach(change => {
            if (isSystemField(change.field)) {
                systemChanges.push(change);
                return;
            }
            const section = getFieldSection(change.field);
            if (section) {
                if (!grouped[section]) {
                    grouped[section] = [];
                }
                grouped[section].push(change);
            } else {
                otherChanges.push(change);
            }
        });

        if (otherChanges.length > 0) {
            grouped[t('changeHistory.otherFields')] = otherChanges;
        }

        if (systemChanges.length > 0) {
            grouped[t('changeHistory.systemFields')] = systemChanges;
        }

        return grouped;
    };

    const renderChangeValue = (value) => {
        if (value === null || value === undefined) {
            return <Text type="secondary" italic>{t('changeHistory.empty')}</Text>;
        }
        if (typeof value === 'boolean') {
            return value ? t('changeHistory.yes') : t('changeHistory.no');
        }
        if (Array.isArray(value)) {
            if (value.length === 0) return <Text type="secondary" italic>{t('changeHistory.empty')}</Text>;
            return String(value.join(', '));
        }
        if (typeof value === 'object' && value !== null && !Array.isArray(value) && (value.en || value.ar)) {
            return localizeField(value);
        }
        if (typeof value === 'object') {
            return String(JSON.stringify(value));
        }
        return String(value);
    };

    // Handle INSERT action — show all created fields
    const renderInsertDetails = (log) => {
        if (!log.newData) return null;

        const flattenData = (obj, prefix = '') => {
            const result = {};
            for (const [key, value] of Object.entries(obj)) {
                const newKey = prefix ? `${prefix}.${key}` : key;
                if (key === 'content' && typeof value === 'object' && !Array.isArray(value)) {
                    Object.assign(result, flattenObject(value));
                } else {
                    result[newKey] = value;
                }
            }
            return result;
        };

        const flattenObject = (obj) => {
            const result = {};
            for (const [key, value] of Object.entries(obj)) {
                if (typeof value === 'object' && value !== null && !Array.isArray(value) && key !== 'content') {
                    for (const [subKey, subValue] of Object.entries(value)) {
                        result[`${key}.${subKey}`] = subValue;
                    }
                } else {
                    result[key] = value;
                }
            }
            return result;
        };

        const fields = flattenData(log.newData);
        const grouped = {};

        Object.entries(fields).forEach(([fieldKey, value]) => {
            if (isSystemField(fieldKey)) return;
            const section = getFieldSection(fieldKey);
            if (!grouped[section]) grouped[section] = [];
            grouped[section].push({ field: fieldKey, value });
        });

        return (
            <div style={{ paddingLeft: 4 }}>
                {Object.entries(grouped).map(([sectionTitle, sectionFields]) => (
                    <Card
                        key={sectionTitle}
                        size="small"
                        title={<Text type="secondary" style={{ fontSize: '0.85em' }}>{sectionTitle}</Text>}
                        style={{ marginBottom: 8, borderRadius: 6 }}
                        bodyStyle={{ padding: '8px 12px' }}
                    >
                        {sectionFields.map((item, idx) => (
                            <Row
                                key={idx}
                                gutter={[8, 4]}
                                align="middle"
                                style={{
                                    padding: '4px 0',
                                    borderBottom: idx < sectionFields.length - 1 ? '1px solid #f5f5f5' : 'none'
                                }}
                            >
                                <Col span={7}>
                                    <Text type="secondary" style={{ fontSize: '0.85em' }}>
                                        {getFieldDisplayName(item.field)}
                                    </Text>
                                </Col>
                                <Col span={17}>
                                    <Text type="success" style={{ fontSize: '0.85em' }}>
                                        {renderChangeValue(item.value)}
                                    </Text>
                                </Col>
                            </Row>
                        ))}
                    </Card>
                ))}
            </div>
        );
    };

    // Handle DELETE action — show all deleted fields
    const renderDeleteDetails = (log) => {
        if (!log.oldData) return null;

        const flattenData = (obj) => {
            const result = {};
            for (const [key, value] of Object.entries(obj)) {
                if (typeof value === 'object' && value !== null && !Array.isArray(value) && key !== 'content') {
                    for (const [subKey, subValue] of Object.entries(value)) {
                        result[`${key}.${subKey}`] = subValue;
                    }
                } else {
                    result[key] = value;
                }
            }
            return result;
        };

        const fields = flattenData(log.oldData);
        const grouped = {};

        Object.entries(fields).forEach(([fieldKey, value]) => {
            if (isSystemField(fieldKey)) return;
            const section = getFieldSection(fieldKey);
            if (!grouped[section]) grouped[section] = [];
            grouped[section].push({ field: fieldKey, value });
        });

        return (
            <div style={{ paddingLeft: 4 }}>
                {Object.entries(grouped).map(([sectionTitle, sectionFields]) => (
                    <Card
                        key={sectionTitle}
                        size="small"
                        title={<Text type="secondary" style={{ fontSize: '0.85em' }}>{sectionTitle}</Text>}
                        style={{ marginBottom: 8, borderRadius: 6 }}
                        bodyStyle={{ padding: '8px 12px' }}
                    >
                        {sectionFields.map((item, idx) => (
                            <Row
                                key={idx}
                                gutter={[8, 4]}
                                align="middle"
                                style={{
                                    padding: '4px 0',
                                    borderBottom: idx < sectionFields.length - 1 ? '1px solid #f5f5f5' : 'none'
                                }}
                            >
                                <Col span={7}>
                                    <Text type="secondary" style={{ fontSize: '0.85em' }}>
                                        {getFieldDisplayName(item.field)}
                                    </Text>
                                </Col>
                                <Col span={17}>
                                    <Text
                                        type="danger"
                                        delete
                                        style={{ fontSize: '0.85em' }}
                                    >
                                        {renderChangeValue(item.value)}
                                    </Text>
                                </Col>
                            </Row>
                        ))}
                    </Card>
                ))}
            </div>
        );
    };

    // Handle UPDATE action — show changed fields with old → new
    const renderUpdateDetails = (log) => {
        const groupedChanges = groupChangesBySection(log.changes);
        const hasChanges = log.changes && log.changes.length > 0;

        if (!hasChanges) return null;

        return (
            <div style={{ paddingLeft: 4 }}>
                {Object.entries(groupedChanges).map(([sectionTitle, sectionChanges]) => (
                    <Card
                        key={sectionTitle}
                        size="small"
                        title={<Text type="secondary" style={{ fontSize: '0.85em' }}>{sectionTitle}</Text>}
                        style={{ marginBottom: 8, borderRadius: 6 }}
                        bodyStyle={{ padding: '8px 12px' }}
                    >
                        {sectionChanges.map((change, idx) => (
                            <Row
                                key={idx}
                                gutter={[8, 4]}
                                align="middle"
                                style={{
                                    padding: '4px 0',
                                    borderBottom: idx < sectionChanges.length - 1 ? '1px solid #f5f5f5' : 'none'
                                }}
                            >
                                <Col span={7}>
                                    <Text type="secondary" style={{ fontSize: '0.85em' }}>
                                        {getFieldDisplayName(change.field)}
                                    </Text>
                                </Col>
                                <Col span={8}>
                                    <Text
                                        type="danger"
                                        delete
                                        style={{ fontSize: '0.85em' }}
                                    >
                                        {renderChangeValue(change.old_value)}
                                    </Text>
                                </Col>
                                <Col span={1} style={{ textAlign: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: '0.75em' }}>→</Text>
                                </Col>
                                <Col span={8}>
                                    <Text
                                        type="success"
                                        style={{ fontSize: '0.85em' }}
                                    >
                                        {renderChangeValue(change.new_value)}
                                    </Text>
                                </Col>
                            </Row>
                        ))}
                    </Card>
                ))}
            </div>
        );
    };

    const renderLogDetails = (log) => {
        switch (log.action?.toUpperCase()) {
            case 'INSERT':
                return renderInsertDetails(log);
            case 'DELETE':
                return renderDeleteDetails(log);
            case 'UPDATE':
            default:
                return renderUpdateDetails(log);
        }
    };

    return (
        <div>
            {isLoading && (
                <Row justify="center" style={{ padding: '20px 0' }}>
                    <Spin />
                </Row>
            )}
            {!isLoading && logs.length === 0 && (
                <Empty
                    description={t('changeHistory.noHistoryFound')}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            )}
            {!isLoading && logs.length > 0 && (
                <Timeline>
                    {logs.map((log) => (
                        <Timeline.Item key={log.id} color={getActionColor(log.action)}>
                            <div style={{ marginLeft: 8 }}>
                                <div style={{ marginBottom: 8 }}>
                                    <Text strong>{getActionLabel(log.action, t)}</Text>
                                    {' '}{t('changeHistory.by')}{' '}
                                    <Text strong>{log.username}</Text>
                                    {' '}{t('changeHistory.at')}{' '}
                                    <Text code>{getDateTimeDisplayText(log.changedAt)}</Text>
                                    {log.identifierName && (
                                        <Tag style={{ marginLeft: 8 }}>{log.identifierName}</Tag>
                                    )}
                                </div>
                                {renderLogDetails(log)}
                            </div>
                        </Timeline.Item>
                    ))}
                </Timeline>
            )}
        </div>
    );
}